import { useCallback, useState, type FC, type ReactNode } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import { Drawer } from "../../shared/Drawer";
import type { Form } from "../types";
import { SectionGroupsNavigation, SectionNavigation } from "./SectionNavigation";
import { Sections } from "./Sections";
import { mapSectionsToSectionGroupsWithSections } from "./mapSectionsToSectionGroupsWithSections";

export type MultiStepFormProps = {
  form: Form;
  header: ReactNode;
};

export const MultiStepForm: FC<MultiStepFormProps> = ({ form, header }) => {
  const { sections } = form;
  const location = useLocation();
  const [firstSection] = sections;

  return (
    <Routes>
      <Route path=":sectionId" element={<MultiStepFormInner form={form} header={header} />} />
      {/* Replace with first section if no match*/}
      <Route path="*" element={<Navigate to={`${firstSection.id}${location.search}`} replace />} />
    </Routes>
  );
};

export const SIDEBAR_WIDTH = "17rem";

const MultiStepFormInner: FC<MultiStepFormProps> = ({ form, header }) => {
  const [headerRef, headerHeight] = useElementHeight();
  const { sections } = form;
  const { sectionId } = useParams();

  const currentSection = sections.find(step => step.id === sectionId);

  /** If the URL-param do not match any section redirect to first section **/
  if (!currentSection) {
    const [firstSection] = sections;
    return <Navigate to={`${firstSection.id}`} replace />;
  }

  return (
    <>
      <Box ref={headerRef} sx={{ zIndex: 1, position: "sticky", top: 0 }}>
        {header}
      </Box>
      <Stack direction="row" sx={{ width: "100%", mx: "auto" }}>
        <MuiDrawer
          variant="permanent"
          sx={{
            zIndex: 0,
            width: SIDEBAR_WIDTH,
            [`& .${drawerClasses.paper}`]: {
              bgcolor: theme => theme.palette.grey["100"],
              width: SIDEBAR_WIDTH,
              overflow: "hidden",
              pt: `${headerHeight}px`,
            },
          }}
        >
          <Box sx={{ overflow: "auto", pt: 2 }}>
            {form.sectionGroups ? (
              <SectionGroupsNavigation
                sectionGroupsWithSections={mapSectionsToSectionGroupsWithSections(sections, form.sectionGroups)}
                currentSectionId={currentSection.id}
              />
            ) : (
              <SectionNavigation sections={sections} currentSectionId={currentSection.id} />
            )}
          </Box>
        </MuiDrawer>

        <Stack sx={{ alignItems: "center", flex: "1", px: Drawer.PADDING_X, maxWidth: "1080px", py: 3, mx: "auto" }}>
          <Sections sections={sections} currentSection={currentSection} />
        </Stack>
      </Stack>
    </>
  );
};

export const useElementHeight = () => {
  const [height, setHeight] = useState<null | number>(null);

  const ref = useCallback((element: HTMLElement | null) => {
    if (element) {
      const { height } = element.getBoundingClientRect();
      setHeight(height);
    }
  }, []);

  return [ref, height] as const;
};
